import type { Organization, WebSite, WithContext } from 'schema-dts';
import boatsetterPaths from 'utils/boatsetterPaths';

export const howWorksData = [
  {
    title: 'Trip protection that works for you',
    text: 'Book with confidence. If bad weather disrupts your trip, you’re eligible for a refund.',
    icon: '/images/homepage/promise/boat.svg',
  },
  {
    title: 'Verified quality standards',
    text: 'Each vessel is reviewed against a checklist for safety, reliability, and cleanliness.',
    icon: '/images/homepage/promise/circle-check.svg',
  },
  {
    title: 'Safe & secure payments',
    text: 'Experience a seamless checkout process, ensuring your transaction is protected.',
    icon: '/images/homepage/promise/lock.svg',
  },
  {
    title: 'Anticipates the unexpected',
    text: 'We’ve designed coverage to handle damages, so you can focus on the adventure.',
    icon: '/images/homepage/promise/life-saver.svg',
  },
];

/**
 * This is separated into two slides for desktop viewing. On mobile, this is flattened.
 */
export const experiencesData = [
  // Slide 1
  [
    {
      title: 'Pontoons',
      s3Path: 'spa/homepage/experiences/Pontoons-040225.png',
      link: boatsetterPaths.pontoonSearch,
      grid: 'pontoon',
      className: 'long',
      mobileObjectPosition: '35%',
    },
    {
      title: 'Yachts',
      s3Path: 'spa/homepage/experiences/Yachts-031126.png',
      link: boatsetterPaths.yachtSearch,
      grid: 'yachts',
      className: 'long',
    },
    {
      title: 'Watersports',
      link: boatsetterPaths.waterSportsLanding,
      s3Path: 'spa/homepage/experiences/WaterSports-040225.png',
      grid: 'cruising',
      className: 'long',
      mobileObjectPosition: '70%',
    },
    {
      title: 'Sailing',
      s3Path: 'spa/homepage/experiences/Sailing-040225.png',
      link: boatsetterPaths.sailingLanding,
      grid: 'sailing',
      objectPosition: '40%',
      mobileObjectPosition: '0',
    },
    {
      title: 'Fishing charters',
      s3Path: 'spa/homepage/experiences/Fishing-Charters-031126.png',
      link: boatsetterPaths.fishingLanding,
      grid: 'fishing',
    },
  ],
  // Slide 2
  [
    {
      title: 'Celebrations',
      s3Path: 'spa/homepage/experiences/Celebrations-040225.png',
      link: boatsetterPaths.celebrationsSearch,
      grid: 'pontoon',
      className: 'long',
      mobileObjectPosition: '35%',
    },
    {
      title: 'Luxury yacht charters',
      s3Path: 'spa/homepage/experiences/LuxuryYachts-040225.png',
      link: boatsetterPaths.luxuryYachtSearch,
      grid: 'yachts',
      mobileObjectPosition: '25%',
    },
    {
      title: 'Cruising',
      link: boatsetterPaths.cruisingSearch,
      s3Path: 'spa/homepage/experiences/Cruising-031126.png',
      grid: 'cruising',
      className: 'long',
      mobileObjectPosition: '70%',
    },
    {
      title: 'Unique finds',
      s3Path: 'spa/homepage/experiences/UniqueFinds-040225.png',
      link: boatsetterPaths.uniqueFinds,
      grid: 'sailing',
      objectPosition: '40%',
      mobileObjectPosition: '0',
    },

    {
      title: 'Snorkeling',
      s3Path: 'spa/homepage/experiences/Snorkeling-040225.png',
      link: boatsetterPaths.snorkelingSearch,
      grid: 'fishing',
    },
  ],
];

export const quotesData = {
  twp: 'The next wave of the sharing economy has arrived...',
  cnbc: 'Boat owners can now offset their entire cost of boat ownership by renting their boats in as few as one to two times a month.',
  twsj: 'Boatsetter makes it easy to book an afternoon sail on a whim...',
  tc: 'Boats have even more potential than homes for the sharing economy. With a few rentals a month, most boats can pay for themselves.',
  cnn: 'Boatsetter makes it simpler for folks to spend time on the water.',
  buzz: 'Boatsetter is a one-stop shop for boat renters...and allows boat owners to offset associated ownership costs.',
};

const organizationSchema: WithContext<Organization> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://www.boatsetter.com/#organization',
  name: 'Boatsetter',
  url: 'https://www.boatsetter.com',
  description:
    'Boatsetter is the largest peer-to-peer boat rental marketplace, connecting boat owners with renters across 700+ locations. Rent boats, yachts, pontoons, and fishing charters with or without a captain.',
  foundingDate: '2012',
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.boatsetter.com/images/homepage/Logo.svg',
    width: '200',
    height: '40',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '333 Las Olas Way, CU1',
    addressLocality: 'Fort Lauderdale',
    addressRegion: 'FL',
    postalCode: '33312',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    url: 'https://help.boatsetter.com',
  },
  sameAs: [
    'https://www.instagram.com/Boatsetter/',
    'https://www.youtube.com/c/Boatsetter',
    'https://www.facebook.com/Boatsetter',
    'https://www.pinterest.com/boatsetter1/',
    'https://www.tiktok.com/@boatsetter',
    'https://www.linkedin.com/company/boatsetter',
  ],
};

export const getWebsiteSchema = () => {
  const schema: WithContext<WebSite> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Boatsetter',
    alternateName: 'Boatsetter.com',
    url: 'https://www.boatsetter.com/',
    publisher: {
      '@id': 'https://www.boatsetter.com/#organization',
    },
  };

  return JSON.stringify(schema);
};

export const getOrganizationSchema = () => JSON.stringify(organizationSchema);
